import React, { useMemo, useCallback, useState, useRef } from 'react';
import Select, { createFilter, components } from 'react-select';
import {selectStyles, Theme} from '../../config/constants';
import { ValueContainer, DropdownIndicator, InputOptionCheckbox, InputOptionDefault } from '../../config/customSelect';
import CustomMenuList from '../Helper/CustomMenuList';
import {Button} from "reactstrap";

const ApplyMenuList = (props) => {
  const {
    children,
    selectProps: {
      hasApply,
      onApplyClick
    },
  } = props;

  return (
    <components.MenuList {...props}>
      {children}

      {hasApply ? (
        <div
          className='position-sticky bottom-0 bg-white p-2 border-top'
        >
          <div className="d-flex gap-2">
            <Button
              block
              className='py-2 button-label'
              size='sm'
              onClick={() => onApplyClick()}
              color={Theme().colors.accentName}>Apply
            </Button>
          </div>
        </div>
      ) : null}
    </components.MenuList>
  );
};

const SelectAllSearch = ({
  options = [],
  onChange,
  isMulti = true,
  selectAllLabel = 'Select All',
  selectAllMatchingLabel = 'Select All Matching',
  enableSelectAll = true,
  value,
  hasApply = false,
  onApplySearch,
  inputValue: externalInputValue = '',
  onInputChange,
  filterOption,
  ...props
}) => {
  const selectRef = useRef(null);
  const [internalInputValue, setInternalInputValue] = useState(externalInputValue);
  const [filteredSearchResults, setFilteredSearchResults] = useState([]);

  const selectAllOption = useMemo(() => ({
    value: '__select_all__',
    label: selectAllLabel,
    isSelectAll: true,
  }), [selectAllLabel]);

  const selectAllMatchingOption = useMemo(() => ({
    value: '__select_all_matching__',
    label: selectAllMatchingLabel,
    isSelectAllMatching: true,
  }), [selectAllMatchingLabel]);

  const optionsWithSelectAll = useMemo(() => {
    if (!isMulti || !enableSelectAll) {
      return options;
    }
    const allOptions = [...options];
    if (internalInputValue) {
      allOptions.unshift(selectAllMatchingOption);
    } else {
      allOptions.unshift(selectAllOption);
    }
    return allOptions;
  }, [options, internalInputValue, isMulti, enableSelectAll, selectAllOption, selectAllMatchingOption]);

  const handleChange = useCallback((newValue) => {
    if (!newValue) {
      onChange?.(newValue);
      return;
    }
    if (Array.isArray(newValue)) {
      const lastValue = newValue[newValue.length - 1];
      if (lastValue?.isSelectAll) {
        const allSelectableOptions = options.filter(
          opt => !opt.isSelectAll && !opt.isSelectAllMatching
        );
        onChange?.(allSelectableOptions);
        return;
      }
      if (lastValue?.isSelectAllMatching) {
        onChange?.(filteredSearchResults);
        return;
      }
      const filtered = newValue.filter(v => !v.isSelectAll && !v.isSelectAllMatching);
      onChange?.(filtered);
    } else {
      onChange?.(newValue);
    }
  }, [onChange, options, filteredSearchResults]);

  const handleInputChange = useCallback((inputVal, actionMeta) => {
    setInternalInputValue(inputVal);
    if (inputVal) {
      const filteredOpts = options.filter(opt => {
        const label = opt.label || '';
        return label.toLowerCase().includes(inputVal.toLowerCase());
      });
      setFilteredSearchResults(filteredOpts);
    } else {
      setFilteredSearchResults([]);
    }

    onInputChange?.(inputVal, actionMeta);
  }, [onInputChange, options]);

  const onApplyClick = useCallback(() => {
    onApplySearch?.();

    // Close menu
    selectRef.current?.blur();
  }, [onApplySearch]);

  return (
    <Select
      {...props}
      ref={selectRef}
      captureMenuScroll={false}
      hideSelectedOptions={false}
      classNamePrefix="custom-select "
      options={optionsWithSelectAll}
      onChange={handleChange}
      onInputChange={handleInputChange}
      value={value}
      isMulti={isMulti}
      closeMenuOnSelect={!(hasApply && isMulti)}
      hasApply={hasApply && isMulti}
      onApplyClick={onApplyClick}
      styles={selectStyles}
      components={{
        Option: (props) => isMulti ? <InputOptionCheckbox {...props} /> : <InputOptionDefault {...props} />,
        ValueContainer,
        DropdownIndicator,
        MenuList: (menuProps) => (
          hasApply && isMulti ? <ApplyMenuList {...menuProps} /> : <CustomMenuList {...menuProps} />
        ),
        ...props.components,
      }}
      filterOption={(option, inputValue) => {
        if (option.data?.isSelectAll || option.data?.isSelectAllMatching) {
          return true;
        }
        return createFilter({ ignoreAccents: false })(option, inputValue);
      }}
    />
  );
};

export default SelectAllSearch;
